import React, { Suspense } from 'react';
import axios from 'axios';

// Always-loaded (lightweight, used immediately)
import { NotificationBell } from '../PriceAlertComponents';
import FeatureGate from '../FeatureGate';
import BottomNavigation from '../BottomNavigation';
import BetaWelcomeModal from '../BetaWelcomeModal';
import FounderWelcomeModal from '../FounderWelcomeModal';
import ProWelcomeModal from '../ProWelcomeModal';
import FreeWelcomeModal from '../FreeWelcomeModal';
import LegalPageModal from '../LegalPageModal';
import WelcomeTour from '../WelcomeTour';
import FeedbackWidget from '../FeedbackWidget';

// Lazy-loaded (heavy, loaded on demand)
const SubmitView = React.lazy(() => import('./SubmitView'));
const HistoryView = React.lazy(() => import('./HistoryView'));
const ShoppingListView = React.lazy(() => import('./ShoppingListView'));
const PriceAlertsPage = React.lazy(() => import('../PriceAlertsPage').then(m => ({ default: m.PriceAlertsPage })));
const EnhancedKitchenAI = React.lazy(() => import('../EnhancedKitchenAI').then(m => ({ default: m.EnhancedKitchenAI })));
const RecipeBrowser = React.lazy(() => import('../RecipeBrowser'));
const InAppPricing = React.lazy(() => import('../InAppPricing'));
const FuelPrices = React.lazy(() => import('../FuelPrices'));
const FuelSavingsTracker = React.lazy(() => import('../FuelSavingsTracker'));
const ReceiptScanner = React.lazy(() => import('../ReceiptScanner'));
const BulkComparison = React.lazy(() => import('../BulkComparison'));
const CartOptimization = React.lazy(() => import('../CartOptimization'));
const LoyaltyWallet = React.lazy(() => import('../LoyaltyWallet'));
const SavingsSection = React.lazy(() => import('../SavingsSection'));
const ReferralDashboard = React.lazy(() => import('../ReferralDashboard'));
const NewsletterPreferences = React.lazy(() => import('../NewsletterPreferences'));
const MyBlogFeed = React.lazy(() => import('../MyBlogFeed'));
const RetailerOAuth = React.lazy(() => import('../RetailerOAuth'));
const FamilyDashboard = React.lazy(() => import('../FamilyDashboard'));
const AdminOverridePanel = React.lazy(() => import('../AdminOverridePanel'));
const SavingsClub = React.lazy(() => import('../SavingsClub'));
const SettingsPage = React.lazy(() => import('../SettingsPage'));
const HelpPage = React.lazy(() => import('../HelpPage'));
const PrivacyPolicyPage = React.lazy(() => import('../../pages/PrivacyPolicyPage'));
const TermsConditionsPage = React.lazy(() => import('../../pages/TermsConditionsPage'));
const CookiePolicyPage = React.lazy(() => import('../../pages/CookiePolicyPage'));
const ContactPage = React.lazy(() => import('../../pages/ContactPage'));
const UpgradeView = React.lazy(() => import('../UpgradeView'));
const ScanTab = React.lazy(() => import('../ScanTab'));
const CompareTab = React.lazy(() => import('../CompareTab'));
const MoreTabSectioned = React.lazy(() => import('../MoreTabSectioned'));
const UnifiedComparePrice = React.lazy(() => import('../UnifiedComparePrice'));

// Loading fallback
const ViewLoader = () => (
  <div className="min-h-screen bg-gray-50 flex items-center justify-center">
    <div className="text-center">
      <div className="w-8 h-8 border-3 border-green-500 border-t-transparent rounded-full animate-spin mx-auto mb-2" />
      <p className="text-sm text-gray-400">Loading...</p>
    </div>
  </div>
);

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = `${BACKEND_URL}/api`;

export default function UserViewRouter({
  activeView, activeTab, handleTabChange,
  userId, email, userData, userTier,
  handleBack, handleLogout, handleNavigation,
  setActiveView, setActiveTab,
  setShowUpgradeModal, setUpgradeReason,
  isAlphaTester,
  // Submit view props
  barcode, setBarcode, productName, setProductName,
  price, setPrice, retailer, setRetailer,
  priceType, setPriceType, originalPrice, setOriginalPrice,
  offerDetails, setOfferDetails,
  scanSuccess, setScanSuccess, scanError,
  loading, showScanner, setShowScanner,
  imagePreview, productImage, setProductImage, setImagePreview,
  quickSubmitMode, setQuickSubmitMode, foundProduct, setFoundProduct,
  lastSubmittedProduct, setLastSubmittedProduct,
  handleBarcodeScanned, handleCloseScan, handleSubmitScan,
  lookupProductByBarcode, handleImageChange, handleBarcodeBlur,
  // History props
  userSubmissions, receiptHistory, setReceiptHistory,
  // Shopping list props
  shoppingListItems, setShoppingListItems,
  // More tab props
  moreReturnSection, setMoreReturnSection,
  showBetaModal, setShowBetaModal,
  showFounderModal, setShowFounderModal, founderModalTier,
  showProModal, setShowProModal, isProFamily,
  showFreeModal, setShowFreeModal,
  legalPageModal, setLegalPageModal,
  showWelcomeTour, handleCloseTour,
}) {

  const goBackToHome = () => {
    setActiveView('dashboard');
    setActiveTab('home');
  };

  const handleUpgradeClick = () => {
    setActiveView('upgrade-view');
  };

  // Submit Scan View
  if (activeView === 'submit') {
    return (
      <SubmitView
        userId={userId} email={email}
        barcode={barcode} setBarcode={setBarcode}
        productName={productName} setProductName={setProductName}
        price={price} setPrice={setPrice}
        retailer={retailer} setRetailer={setRetailer}
        priceType={priceType} setPriceType={setPriceType}
        originalPrice={originalPrice} setOriginalPrice={setOriginalPrice}
        offerDetails={offerDetails} setOfferDetails={setOfferDetails}
        scanSuccess={scanSuccess} setScanSuccess={setScanSuccess} scanError={scanError}
        loading={loading} showScanner={showScanner} setShowScanner={setShowScanner}
        imagePreview={imagePreview} productImage={productImage}
        setProductImage={setProductImage} setImagePreview={setImagePreview}
        quickSubmitMode={quickSubmitMode} setQuickSubmitMode={setQuickSubmitMode}
        foundProduct={foundProduct} setFoundProduct={setFoundProduct}
        lastSubmittedProduct={lastSubmittedProduct} setLastSubmittedProduct={setLastSubmittedProduct}
        handleBack={handleBack} handleLogout={handleLogout}
        handleBarcodeScanned={handleBarcodeScanned} handleCloseScan={handleCloseScan}
        handleSubmitScan={handleSubmitScan} lookupProductByBarcode={lookupProductByBarcode}
        handleImageChange={handleImageChange} handleBarcodeBlur={handleBarcodeBlur}
        userTier={userTier} userData={userData}
        activeTab={activeTab} handleTabChange={handleTabChange}
      />
    );
  }

  // Compare View
  if (activeView === 'compare') {
    return (
      <>
        <div className="min-h-screen bg-gray-50 pb-20">
          <header className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white shadow-lg">
            <div className="max-w-7xl mx-auto px-4 py-4 flex justify-between items-center">
              <div className="flex items-center space-x-3">
                <button 
                  onClick={handleBack}
                  className="flex items-center gap-2 px-3 py-1.5 bg-white/20 hover:bg-white/30 rounded-lg transition text-sm font-medium"
                >
                  ← Compare
                </button>
                <h1 className="text-xl font-bold">Compare Prices</h1>
              </div>
              <div className="flex items-center space-x-4">
                <NotificationBell userId={userId} />
                <button onClick={handleLogout} className="text-sm hover:text-blue-200 font-medium">
                  Logout
                </button>
              </div>
            </div>
          </header>
          <UnifiedComparePrice userId={userId} onNavigate={handleNavigation} />
          <BottomNavigation activeTab={activeTab} onTabChange={handleTabChange} />
        </div>
      </>
    );
  }

  // History View
  if (activeView === 'history') {
    return (
      <HistoryView
        userId={userId} email={email}
        userSubmissions={userSubmissions} receiptHistory={receiptHistory}
        setReceiptHistory={setReceiptHistory}
        handleBack={handleBack} setActiveView={setActiveView}
        activeTab={activeTab} handleTabChange={handleTabChange}
      />
    );
  }

  // Price Alerts View
  if (activeView === 'alerts') {
    return (
      <>
        <div className="min-h-screen bg-gray-50 pb-20">
          <header className="bg-gradient-to-r from-amber-500 to-orange-500 text-white py-4 shadow-lg">
            <div className="max-w-7xl mx-auto px-4 flex justify-between items-center">
              <div className="flex items-center gap-3">
                <button onClick={handleBack} className="px-3 py-1.5 bg-white bg-opacity-20 hover:bg-opacity-30 rounded-lg text-sm font-medium">
                  ← More
                </button>
                <div>
                  <h1 className="text-xl font-bold">Price Alerts</h1>
                  <p className="text-amber-100 text-sm">{email}</p>
                </div>
              </div>
              <NotificationBell userId={userId} />
            </div>
          </header>
          <main className="max-w-5xl mx-auto px-4 py-6">
            <PriceAlertsPage userId={userId} onNavigate={handleNavigation} />
          </main>
          <BottomNavigation activeTab={activeTab} onTabChange={handleTabChange} />
        </div>
      </>
    );
  }

  // Kitchen AI View
  if (activeView === 'kitchen') {
    return (
      <>
        <FeatureGate userData={userData} userTier={userTier} requiredTier="pro" featureName="Kitchen AI" onUpgradeClick={handleUpgradeClick}>
          <EnhancedKitchenAI userId={userId} onBack={handleBack} />
        </FeatureGate>
        <BottomNavigation activeTab={activeTab} onTabChange={handleTabChange} />
      </>
    );
  }

  // Recipes View
  if (activeView === 'recipes') {
    return (
      <>
        <RecipeBrowser onBack={handleBack} />
        <BottomNavigation activeTab={activeTab} onTabChange={handleTabChange} />
      </>
    );
  }

  // Pricing Page View
  if (activeView === 'pricing') {
    return (
      <>
        <InAppPricing onBack={handleBack} userId={userId} userData={userData} />
        <BottomNavigation activeTab={activeTab} onTabChange={handleTabChange} />
      </>
    );
  }

  // Fuel Prices View
  if (activeView === 'fuel-prices') {
    return (
      <>
        <FeatureGate userData={userData} userTier={userTier} requiredTier="pro" featureName="Fuel Price Finder" onUpgradeClick={handleUpgradeClick}>
          <FuelPrices 
            userId={userId} 
            subscriptionTier={userData?.subscription_tier || 'free'}
            isBetaTester={userData?.is_beta_tester || userData?.is_alpha_tester || false}
            onNavigate={handleNavigation}
            onBack={handleBack}
          />
        </FeatureGate>
        <BottomNavigation activeTab={activeTab} onTabChange={handleTabChange} />
      </>
    );
  }

  // Fuel Savings Tracker
  if (activeView === 'fuel-savings') {
    return (
      <>
        <FuelSavingsTracker userId={userId} onBack={handleBack} />
        <BottomNavigation activeTab={activeTab} onTabChange={handleTabChange} />
      </>
    );
  }

  // Receipt Scanner View
  if (activeView === 'receipt') {
    return (
      <>
        <div className="min-h-screen bg-gray-50 pb-20">
          <header className="bg-gradient-to-r from-emerald-500 to-teal-600 text-white py-4 shadow-lg">
            <div className="max-w-7xl mx-auto px-4 flex justify-between items-center">
              <div className="flex items-center gap-3">
                <button onClick={handleBack} className="px-3 py-1.5 bg-white bg-opacity-20 hover:bg-opacity-30 rounded-lg text-sm font-medium">
                  ← Scan
                </button>
                <div>
                  <h1 className="text-xl font-bold">Receipt Scanner</h1>
                  <p className="text-emerald-100 text-sm">{email}</p>
                </div>
              </div>
            </div>
          </header>
          <main className="max-w-2xl mx-auto px-4 py-8">
            <ReceiptScanner 
              userId={userId}
              onPricesExtracted={() => {
                axios.get(`${API}/price-hub/my-receipts`, {
                  params: { user_id: userId, limit: 50 },
                  withCredentials: true
                }).then(res => {
                  if (res?.data) setReceiptHistory(res.data);
                }).catch(console.error);
              }}
              onViewHistory={() => setActiveView('history')}
            />
            <div className="mt-8 bg-white rounded-xl shadow-md p-6">
              <h3 className="font-semibold text-gray-800 mb-4">How it works</h3>
              <div className="space-y-3 text-sm text-gray-600">
                <div className="flex items-start gap-3">
                  <span className="flex-shrink-0 w-6 h-6 bg-emerald-100 rounded-full flex items-center justify-center text-emerald-600 font-semibold">1</span>
                  <p>Take a photo of your shopping receipt or upload an existing image</p>
                </div>
                <div className="flex items-start gap-3">
                  <span className="flex-shrink-0 w-6 h-6 bg-emerald-100 rounded-full flex items-center justify-center text-emerald-600 font-semibold">2</span>
                  <p>Our OCR technology extracts all items and prices automatically</p>
                </div>
                <div className="flex items-start gap-3">
                  <span className="flex-shrink-0 w-6 h-6 bg-emerald-100 rounded-full flex items-center justify-center text-emerald-600 font-semibold">3</span>
                  <p>Prices are saved to help other shoppers find the best deals</p>
                </div>
                <div className="flex items-start gap-3">
                  <span className="flex-shrink-0 w-6 h-6 bg-amber-100 rounded-full flex items-center justify-center text-amber-600 font-semibold">★</span>
                  <p><strong>Every receipt</strong> personalises your Weekly Shop and sharpens your price alerts.</p>
                </div>
              </div>
            </div>
          </main>
        </div>
        <BottomNavigation activeTab={activeTab} onTabChange={handleTabChange} />
      </>
    );
  }

  // Bulk Comparison View
  if (activeView === 'bulk') {
    return (
      <>
        <div className="min-h-screen bg-gray-50 pb-20">
          <header className="bg-gradient-to-r from-indigo-500 to-purple-600 text-white py-4 shadow-lg">
            <div className="max-w-7xl mx-auto px-4 flex justify-between items-center">
              <div className="flex items-center gap-3">
                <button onClick={handleBack} className="px-3 py-1.5 bg-white bg-opacity-20 hover:bg-opacity-30 rounded-lg text-sm font-medium">
                  ← Compare
                </button>
                <div>
                  <h1 className="text-xl font-bold">Bulk Compare</h1>
                  <p className="text-purple-100 text-sm">{email}</p>
                </div>
              </div>
            </div>
          </header>
          <main className="max-w-5xl mx-auto px-4 py-6">
            <FeatureGate userData={userData} userTier={userTier} requiredTier="pro" featureName="Bulk Comparison" onUpgradeClick={handleUpgradeClick}>
              <BulkComparison userId={userId} />
            </FeatureGate>
          </main>
        </div>
        <BottomNavigation activeTab={activeTab} onTabChange={handleTabChange} />
      </>
    );
  }

  // Cart Optimization View
  if (activeView === 'cart') {
    return (
      <FeatureGate userData={userData} userTier={userTier} requiredTier="pro" featureName="Cart Optimizer" onUpgradeClick={handleUpgradeClick}>
        <CartOptimization userId={userId} email={email} onNavigate={handleNavigation} />
      </FeatureGate>
    );
  }

  // Shopping List View
  if (activeView === 'shopping-list') {
    return (
      <ShoppingListView
        userId={userId}
        shoppingListItems={shoppingListItems} setShoppingListItems={setShoppingListItems}
        showScanner={showScanner} setShowScanner={setShowScanner}
        scanSuccess={scanSuccess} setScanSuccess={setScanSuccess} scanError={scanError}
        handleBack={handleBack}
        userTier={userTier} userData={userData}
      />
    );
  }

  // Loyalty Wallet View
  if (activeView === 'wallet') {
    return (
      <>
        <FeatureGate userData={userData} userTier={userTier} requiredTier="pro" featureName="Loyalty Wallet" onUpgradeClick={handleUpgradeClick}>
          <LoyaltyWallet userId={userId} backendUrl={BACKEND_URL} onNavigate={handleNavigation} onBack={handleBack} />
        </FeatureGate>
        <BottomNavigation activeTab={activeTab} onTabChange={handleTabChange} />
      </>
    );
  }

  // Savings View
  if (activeView === 'savings') {
    return (
      <>
        <SavingsSection 
          userId={userId} userTier={userTier} userData={userData}
          backendUrl={BACKEND_URL} onNavigate={handleNavigation}
          onUpgradeClick={() => { setActiveView('upgrade-view'); }}
        />
        <BottomNavigation activeTab={activeTab} onTabChange={handleTabChange} />
      </>
    );
  }

  // Referral View
  if (activeView === 'referral') {
    return (
      <>
        <ReferralDashboard userId={userId} userEmail={email} onNavigate={handleNavigation} onBack={handleBack} />
        <BottomNavigation activeTab={activeTab} onTabChange={handleTabChange} />
      </>
    );
  }

  // Newsletter View
  if (activeView === 'newsletter') {
    return (
      <>
        <div className="min-h-screen bg-gray-50 pb-20">
          <header className="bg-gradient-to-r from-violet-600 to-purple-600 text-white shadow-lg">
            <div className="max-w-7xl mx-auto px-4 py-4 flex items-center gap-3">
              <button onClick={handleBack} className="flex items-center gap-2 px-3 py-1.5 bg-white/20 hover:bg-white/30 rounded-lg transition text-sm font-medium">
                ← Back
              </button>
              <h1 className="text-xl font-bold">Newsletter Preferences</h1>
            </div>
          </header>
          <div className="max-w-2xl mx-auto px-4 py-6">
            <NewsletterPreferences userId={userId} userEmail={userData?.email} />
          </div>
        </div>
        <BottomNavigation activeTab={activeTab} onTabChange={handleTabChange} />
      </>
    );
  }

  // Blog Feed View
  if (activeView === 'my-blog') {
    return (
      <>
        <MyBlogFeed userId={userId} onBack={handleBack} />
        <BottomNavigation activeTab={activeTab} onTabChange={handleTabChange} />
      </>
    );
  }

  // Hair & Beauty View — HIDDEN (feature paused, focusing on grocery & fuel)
  // Business Portal View — HIDDEN (depends on Hair & Beauty)

  // Retailer OAuth View
  if (activeView === 'retailers') {
    return (
      <>
        <RetailerOAuth userId={userId} onNavigate={handleNavigation} />
        <BottomNavigation activeTab={activeTab} onTabChange={handleTabChange} />
      </>
    );
  }

  // Family Dashboard View
  if (activeView === 'family') {
    return (
      <>
        <FamilyDashboard userId={userId} onNavigate={handleNavigation} onBack={handleBack} />
        <BottomNavigation activeTab={activeTab} onTabChange={handleTabChange} />
      </>
    );
  }

  // Admin Override Panel
  if (activeView === 'admin-override') {
    return <AdminOverridePanel />;
  }

  // Settings Page
  if (activeView === 'settings') {
    return (
      <>
        <SettingsPage userId={userId} userData={userData} onBack={handleBack} onNavigate={handleNavigation} />
        <BottomNavigation activeTab={activeTab} onTabChange={handleTabChange} />
      </>
    );
  }

  // Help Page
  if (activeView === 'help') {
    return (
      <>
        <HelpPage onBack={handleBack} onNavigate={handleNavigation} />
        <BottomNavigation activeTab={activeTab} onTabChange={handleTabChange} />
      </>
    );
  }

  // Legal Pages
  if (activeView === 'privacy') {
    return (<><PrivacyPolicyPage onBack={handleBack} /><BottomNavigation activeTab={activeTab} onTabChange={handleTabChange} /></>);
  }
  if (activeView === 'terms') {
    return (<><TermsConditionsPage onBack={handleBack} /><BottomNavigation activeTab={activeTab} onTabChange={handleTabChange} /></>);
  }
  if (activeView === 'cookies') {
    return (<><CookiePolicyPage onBack={handleBack} /><BottomNavigation activeTab={activeTab} onTabChange={handleTabChange} /></>);
  }
  if (activeView === 'contact') {
    return (<><ContactPage onBack={handleBack} /><BottomNavigation activeTab={activeTab} onTabChange={handleTabChange} /></>);
  }

  // Savings Club (Points & Rewards)
  if (activeView === 'savings-club' || activeView === 'rewards') {
    return (
      <>
        <Suspense fallback={<ViewLoader />}>
          <SavingsClub userData={userData} onBack={handleBack} />
        </Suspense>
        <BottomNavigation activeTab={activeTab} onTabChange={handleTabChange} />
      </>
    );
  }

  // Upgrade View
  if (activeView === 'upgrade-view') {
    return <UpgradeView userData={userData} onBack={handleBack} />;
  }

  // Scan Tab Parent
  if (activeView === 'scan-parent') {
    return (
      <>
        <ScanTab userData={userData} onNavigate={(view) => { setActiveTab('scan'); setActiveView(view); }} onBackToHome={goBackToHome} />
        <BottomNavigation activeTab={activeTab} onTabChange={handleTabChange} />
      </>
    );
  }

  // Compare Tab Parent
  if (activeView === 'compare-parent') {
    return (
      <>
        <CompareTab 
          userData={userData}
          onNavigate={(view) => {
            // IMPORTANT: Set activeTab to 'compare' so handleBack knows where to return
            setActiveTab('compare');
            if (view === 'compare-single') setActiveView('compare');
            else setActiveView(view);
          }}
          onBackToHome={goBackToHome}
        />
        <BottomNavigation activeTab={activeTab} onTabChange={handleTabChange} />
      </>
    );
  }

  // More Tab
  if (activeView === 'more') {
    const isProUser = isAlphaTester || ['INDIVIDUAL', 'FAMILY', 'VIP_FOUNDER', 'EARLY_FOUNDER'].includes(userData?.subscription_tier);
    const isTrialUser = userData?.trial_active || userData?.subscription_tier === 'PRO_TRIAL';
    
    return (
      <>
        <MoreTabSectioned 
          userData={userData}
          onNavigate={(view) => { setMoreReturnSection(null); handleNavigation(view); }}
          isProUser={isProUser}
          isTrialUser={isTrialUser}
          onShowMission={() => setShowBetaModal(true)}
          onBackToHome={goBackToHome}
          initialSection={moreReturnSection}
        />
        <BottomNavigation activeTab={activeTab} onTabChange={handleTabChange} />
        <FeedbackWidget userId={userId} />
        {showBetaModal && <BetaWelcomeModal onClose={() => setShowBetaModal(false)} userName={userData?.name || 'Explorer'} />}
        {showFounderModal && <FounderWelcomeModal onClose={() => setShowFounderModal(false)} userName={userData?.name || 'Founder'} tier={founderModalTier} />}
        {showProModal && <ProWelcomeModal onClose={() => setShowProModal(false)} userName={userData?.name || 'Pro Member'} isFamily={isProFamily} />}
        {showFreeModal && <FreeWelcomeModal onClose={() => setShowFreeModal(false)} userName={userData?.name || 'there'} onUpgrade={() => handleNavigation('upgrade')} />}
        {legalPageModal && <LegalPageModal page={legalPageModal} onClose={() => setLegalPageModal(null)} />}
        {showWelcomeTour && <WelcomeTour userData={userData} onClose={handleCloseTour} />}
      </>
    );
  }

  // Fallback — should not normally reach here
  return null;
}
